import React from "react";
import { FinetuneDataset, TrainingMethod } from "../../../../model_gen/finetuning";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { FileTextIcon } from "lucide-react";
import ReviewCompositionCard from "./ReviewCompositionCard";
import ReviewFinetuneSettingsCard from "./ReviewFinetuneSettingsCard";
import ReviewBenchmarkTestsCard from "./ReviewBenchmarkTestsCard";

interface ReviewStepProps {
  trainingMethod?: TrainingMethod;
  dpoBeta?: number;
  grpoNumGenerations?: number;
  grpoMaxCompletionLength?: number;
  grpoRewardFunction?: string;
  selectedDataset: string;
  exportPrompt: string;
  fourbit: boolean;
  baseModel: string;
  baseFinetuneRunId?: number | null;
  displayName: string;
  description?: string;
  promptDeletions: string[];
  warmupSteps: number;
  warmupRatio: number;
  learningRate: number;
  perDeviceTrainBatchSize: number;
  gradientAccumulationSteps: number;
  trainOnResponsesOnly: boolean;
  lrSchedulerType?: string;
  trainSplit: number;
  alpha?: number;
  rank?: number;
  epochs?: number;
  weightDecay?: number;
  dropout?: number;
  rslora?: boolean;
  numGpus?: number;
  selectedSubDataset?: FinetuneDataset | null;
  benchmarkTestSetIds?: number[];
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  trainingMethod = "SFT",
  dpoBeta,
  grpoNumGenerations,
  grpoMaxCompletionLength,
  grpoRewardFunction,
  selectedDataset,
  exportPrompt,
  fourbit,
  baseModel,
  baseFinetuneRunId,
  displayName,
  description,
  warmupRatio,
  learningRate,
  perDeviceTrainBatchSize,
  gradientAccumulationSteps,
  trainOnResponsesOnly,
  lrSchedulerType,
  trainSplit,
  alpha,
  rank,
  epochs,
  weightDecay,
  dropout,
  rslora,
  numGpus = 1,
  benchmarkTestSetIds,
}) => {
  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Review Configuration
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Review your finetune run configuration before creating
        </p>
      </div>

      <ReviewCompositionCard compositionId={selectedDataset} />

      <ReviewFinetuneSettingsCard
        trainingMethod={trainingMethod}
        dpoBeta={dpoBeta}
        grpoNumGenerations={grpoNumGenerations}
        grpoMaxCompletionLength={grpoMaxCompletionLength}
        grpoRewardFunction={grpoRewardFunction}
        displayName={displayName}
        description={description}
        baseModel={baseModel}
        baseFinetuneRunId={baseFinetuneRunId}
        fourbit={fourbit}
        warmupRatio={warmupRatio}
        learningRate={learningRate}
        perDeviceTrainBatchSize={perDeviceTrainBatchSize}
        gradientAccumulationSteps={gradientAccumulationSteps}
        trainOnResponsesOnly={trainOnResponsesOnly}
        lrSchedulerType={lrSchedulerType}
        trainSplit={trainSplit}
        alpha={alpha}
        rank={rank}
        epochs={epochs}
        weightDecay={weightDecay}
        dropout={dropout}
        rslora={rslora}
        numGpus={numGpus}
      />

      {exportPrompt && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <FileTextIcon className="w-5 h-5 text-maitai-green" />
              Prompt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-4">
              <pre className="text-sm font-mono text-foreground whitespace-pre-wrap break-words">
                {exportPrompt}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {benchmarkTestSetIds && benchmarkTestSetIds.length > 0 && (
        <ReviewBenchmarkTestsCard benchmarkTestSetIds={benchmarkTestSetIds} />
      )}
    </div>
  );
};

export default ReviewStep;
